import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MatchFoundData } from '../../../../shared/types/game';
import { useNavigate } from 'react-router-dom';
import styles from './MatchingScreen.module.css';
import { Socket } from 'socket.io-client';

interface MatchingScreenProps {
  playerName: string;
  playerId: string;
  onMatchFound: (data: MatchFoundData) => void;
  socket: Socket | null;
  isRegistered: boolean;
  onLogout: () => void;
  isSocketConnected: boolean;
  isPlayerConnectedToServer: boolean;
}

const MatchingScreen: React.FC<MatchingScreenProps> = ({ 
  playerName, 
  playerId, 
  onMatchFound, 
  socket, 
  isRegistered, 
  onLogout,
  isSocketConnected,
  isPlayerConnectedToServer
}) => {
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  const leaveQueue = useCallback(() => {
    if (socket && isSearching) {
      console.log(`MatchingScreen: Player ${playerName} (ID: ${playerId}) sent leaveQueue request.`);
      socket.emit('match:leaveQueue', { playerId });
      setIsSearching(false);
      setErrorMessage('');
    }
  }, [socket, isSearching, playerId, playerName]);

  const handleBeforeUnload = useCallback(() => {
    if (isSearching) {
      leaveQueue();
    }
  }, [isSearching, leaveQueue]);

  useEffect(() => {
    if (socket && isSocketConnected && isPlayerConnectedToServer) {
      socket.on('match:found', (data: MatchFoundData) => {
        console.log('MatchingScreen: マッチングが成立しました！', data);
        setIsSearching(false);
        setErrorMessage('');
        onMatchFound(data);
      });

      socket.on('match:leftQueue', (data: { success: boolean; message?: string }) => {
        if (data.success) {
          console.log('MatchingScreen: マッチングキューから正常に離脱しました。');
          setErrorMessage(data.message || 'マッチング検索をキャンセルしました。');
        } else {
          console.error('MatchingScreen: マッチングキューからの離脱に失敗しました:', data.message);
          setErrorMessage(data.message || 'マッチングキューからの離脱に失敗しました。');
        }
        setIsSearching(false);
      });

      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      if (socket) {
        socket.off('match:found');
        socket.off('match:leftQueue');
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [socket, onMatchFound, isSocketConnected, isPlayerConnectedToServer, handleBeforeUnload]);

  const handleStartGame = () => {
    setErrorMessage('');

    if (!socket || !isSocketConnected || !isPlayerConnectedToServer) {
      setErrorMessage('サーバーに接続していません。しばらく待ってから再度お試しください。');
      console.log('MatchingScreen: ソケット未接続またはプレイヤー未接続。', { socket, isSocketConnected, isPlayerConnectedToServer });
      return;
    }
    setIsSearching(true);
    socket.emit('match:joinQueue', { playerId, playerName });
    console.log(`MatchingScreen: Player ${playerName} (ID: ${playerId}) sent joinQueue request.`);
  };

  const handleGoToMyPage = () => {
    if (isSearching) {
      leaveQueue();
    }
    navigate('/mypage');
  };

  const handleGoToAuthPage = () => {
    if (isSearching) {
      leaveQueue();
    }
    console.log('MatchingScreen: 「ログイン」ボタンがクリックされました。/authへ遷移します。');
    navigate('/auth');
  };

  const handleLogoutClick = () => {
    if (isSearching) {
      leaveQueue();
    }
    onLogout();
  };

  const handleCancelSearch = () => {
    leaveQueue();
  };

  // プレイヤー名の表示を最適化するロジック
  const displayedPlayerName = useMemo(() => {
    const MAX_LENGTH_TO_ELLIPSIZE = 12; // 省略記号(...)を表示し始める文字数

    if (playerName.length > MAX_LENGTH_TO_ELLIPSIZE) {
      return `${playerName.substring(0, MAX_LENGTH_TO_ELLIPSIZE - 3)}...`;
    }
    return playerName;
  }, [playerName]);

  // 名前が長い場合に適用するクラスを決定
  const playerNameClass = useMemo(() => {
    const baseClass = styles.playerNameHighlight;
    const MAX_LENGTH_TO_SHRINK_FONT = 8; // ここも合わせる
    if (playerName.length > MAX_LENGTH_TO_SHRINK_FONT) {
      return `${baseClass} ${styles.playerNameShrink}`;
    }
    return baseClass;
  }, [playerName]);

  return (
    <div className={styles.container}>
      <h2 className={styles.welcomeMessage}>
        <span className={styles.welcomePrefix}>こんにちは、</span>
        <span className={playerNameClass}>{displayedPlayerName}</span>
        {!isRegistered && <span className={styles.guestSuffix}> (ゲスト)</span>}
        <span className={styles.welcomeSuffix}>さん</span>
      </h2>
      <div className={styles.buttonGroup}>
        {!isSearching ? (
          <button onClick={handleStartGame} className={styles.startButton}>
            ゲームスタート
          </button>
        ) : (
          <>
            <p className={styles.searchingText}>対戦相手を探しています...</p>
            <div className={styles.spinner}></div>
            <button onClick={handleCancelSearch} className={styles.cancelButton}>
              キャンセル
            </button>
          </>
        )}
        
        {!isSearching && (
          <>
            <button onClick={handleGoToMyPage} className={styles.myPageButton}>マイページ</button>
            {isRegistered ? (
              <button onClick={handleLogoutClick} className={styles.logoutButton}>ログアウト</button>
            ) : (
              <button onClick={handleGoToAuthPage} className={styles.loginPageButton}>ログイン/新規登録</button>
            )}
          </>
        )}
      </div>
      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
    </div>
  );
};

export default MatchingScreen;